import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Search, Menu, User, Sparkles, X, Compass, Crown, Check, Globe, LogOut, Settings, Zap, MessageSquare, Bell, Heart, Trophy, Award, Store } from 'lucide-react';
import { cn } from '@/lib/utils';
import { INITIAL_CATEGORIES } from '@/data/initialData';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ModeToggle } from './ModeToggle';
import { LanguageSelector } from './LanguageSelector';
import { useUnlock } from '@/contexts/UnlockContext';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/db/supabase';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { 
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { formatDistanceToNow } from 'date-fns';
import { zhCN } from 'date-fns/locale';
import { toast } from 'sonner';

export const Navbar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isUnlocked } = useUnlock();
  const { user, profile, signOut } = useAuth();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [notifications, setNotifications] = useState<any[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [config, setConfig] = useState<any>(null);

  const fetchConfig = async () => {
    const { data } = await supabase.from('system_config').select('*').single();
    if (data) setConfig(data);
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchNotifications = async () => {
    if (!user) return;
    const { data } = await supabase
      .from('notifications')
      .select('*, profiles:sender_id(username, avatar_url, level, points)')
      .eq('receiver_id', user.id)
      .order('created_at', { ascending: false })
      .limit(8);
    setNotifications(data || []);
    setUnreadCount(data?.filter((n: any) => !n.is_read).length || 0);
  };

  useEffect(() => {
    if (user) {
      fetchNotifications();
      const channel = supabase
        .channel('notifications')
        .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'notifications', filter: `receiver_id=eq.${user.id}` }, () => {
          fetchNotifications();
        })
        .subscribe();
      return () => { supabase.removeChannel(channel); };
    }
  }, [user?.id]);

  const markAsRead = async (id: string) => {
    await supabase.from('notifications').update({ is_read: true }).eq('id', id);
    fetchNotifications();
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchValue.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchValue.trim())}`);
      setIsSearchOpen(false);
      setSearchValue('');
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      toast.success('已退出登录');
      navigate('/');
    } catch (error) {
      toast.error('退出失败，请重试');
    }
  };

  return (
    <nav className="sticky top-0 z-50 w-full border-b bg-background/80 backdrop-blur-md">
      <div className="container mx-auto flex h-16 items-center justify-between px-4 md:px-6">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 font-bold text-xl tracking-tight shrink-0">
          <div className="bg-primary p-1.5 rounded-lg">
            <Sparkles className="h-5 w-5 text-primary-foreground" />
          </div>
          <span className="hidden sm:inline-block gradient-text">万象聚聚光2135c</span>
          <span className="sm:hidden gradient-text">万象</span>
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center gap-1 mx-4 overflow-x-auto no-scrollbar max-w-[50%]">
          {INITIAL_CATEGORIES.map((cat) => (
            <Link
              key={cat.id}
              to={`/category/${cat.id}`}
              className={cn(
                "px-3 py-2 text-sm font-medium transition-colors whitespace-nowrap rounded-md hover:text-primary hover:bg-primary/5",
                location.pathname === `/category/${cat.id}` 
                  ? "text-primary bg-primary/10" 
                  : "text-muted-foreground"
              )}
            >
              {cat.name}
            </Link>
          ))}
          <Link
            to="/community"
            className={cn(
              "px-3 py-2 text-sm font-medium transition-colors whitespace-nowrap rounded-md hover:text-primary hover:bg-primary/5 flex items-center gap-1",
              location.pathname === '/community'
                ? "text-primary bg-primary/10" 
                : "text-muted-foreground"
            )}
          >
            <MessageSquare className="h-3.5 w-3.5" />
            社区
          </Link>
          <Link
            to="/ai-hub"
            className={cn(
              "px-3 py-2 text-sm font-medium transition-colors whitespace-nowrap rounded-md hover:text-primary hover:bg-primary/5 flex items-center gap-1",
              location.pathname === '/ai-hub'
                ? "text-primary bg-primary/10" 
                : "text-muted-foreground"
            )}
          >
            <Sparkles className="h-3.5 w-3.5" />
            AI助手
          </Link>
          <Link
            to="/ai-creator"
            className={cn(
              "px-3 py-2 text-sm font-medium transition-colors whitespace-nowrap rounded-md hover:text-primary hover:bg-primary/5 flex items-center gap-1",
              location.pathname === '/ai-creator'
                ? "text-primary bg-primary/10" 
                : "text-muted-foreground"
            )}
          >
            <Zap className="h-3.5 w-3.5" />
            AI创作
          </Link>
        </div>

        {/* Right Actions */}
        <div className="flex items-center gap-2 shrink-0">
          <div className="relative flex items-center">
            {isSearchOpen ? (
              <form onSubmit={handleSearch} className="flex items-center absolute right-0 top-1/2 -translate-y-1/2 z-10 w-[200px] sm:w-[300px] animate-in slide-in-from-right-4 duration-200">
                <Input
                  autoFocus
                  placeholder="搜索教程、工具..."
                  className="h-9 pr-8"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                />
                <Button 
                  type="button"
                  variant="ghost" 
                  size="icon" 
                  className="h-7 w-7 absolute right-1" 
                  onClick={() => setIsSearchOpen(false)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </form>
            ) : (
              <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => setIsSearchOpen(true)}>
                <Search className="h-5 w-5" />
              </Button>
            )}
          </div>

          <div className="hidden sm:block">
            <ModeToggle />
          </div>

          <div className="hidden sm:block">
            <LanguageSelector />
          </div>

          {user && (
            <Popover onOpenChange={(open) => { 
              if (open && unreadCount > 0) { 
                const unread = notifications.filter(n => !n.is_read).map(n => n.id);
                if (unread.length > 0) {
                  Promise.all(unread.map(id => markAsRead(id)));
                }
              } 
            }}>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" className="relative h-9 w-9 rounded-full hover:bg-muted">
                  <Bell className="h-5 w-5 text-muted-foreground" />
                  {unreadCount > 0 && (
                    <span className="absolute top-1.5 right-1.5 h-2 w-2 bg-red-500 rounded-full border-2 border-background" />
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-0 rounded-xl overflow-hidden shadow-2xl border-border/50 bg-background/95 backdrop-blur-md" align="end">
                <div className="p-4 border-b border-border/50 bg-primary/5 flex items-center justify-between">
                  <h4 className="text-sm font-bold flex items-center gap-2">
                    <Bell className="h-4 w-4 text-primary" />
                    消息通知
                  </h4>
                  {unreadCount > 0 && <Badge className="bg-primary text-white text-[10px] px-1.5 h-4 min-w-4 flex items-center justify-center">{unreadCount}</Badge>}
                </div>
                <div className="max-h-[400px] overflow-y-auto">
                  {notifications.length > 0 ? (
                    notifications.map((n) => (
                      <div 
                        key={n.id} 
                        className={cn(
                          "p-4 border-b border-border/30 hover:bg-muted/30 transition-colors cursor-pointer flex gap-3",
                          !n.is_read && "bg-primary/5"
                        )}
                        onClick={() => {
                          if (n.post_id) navigate(`/community/post/${n.post_id}`);
                          markAsRead(n.id);
                        }}
                      >
                        <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                          {n.type.includes('like') ? <Heart className="h-4 w-4 text-red-500 fill-current" /> : <MessageSquare className="h-4 w-4 text-primary" />}
                        </div>
                        <div className="flex-1 space-y-1">
                          <p className="text-xs leading-relaxed">
                            <span className="font-bold text-foreground">{n.profiles?.username || '用户'}</span>
                            <span className="text-muted-foreground ml-1">
                              {n.type === 'like_post' ? '赞了你的帖子' : n.type === 'like_comment' ? '赞了你的评论' : n.type === 'comment' ? '评论了你的帖子' : '回复了你的评论'}
                            </span>
                          </p>
                          {n.content && <p className="text-[10px] text-muted-foreground line-clamp-1 italic">"{n.content}"</p>}
                          <p className="text-[10px] text-muted-foreground">
                            {formatDistanceToNow(new Date(n.created_at), { locale: zhCN, addSuffix: true })}
                          </p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="py-12 text-center text-muted-foreground">
                      <Bell className="h-10 w-10 mx-auto mb-3 opacity-20" />
                      <p className="text-sm">暂无新消息</p>
                    </div>
                  )}
                </div>
                <div className="p-3 border-t border-border/50 text-center">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="text-xs font-bold text-primary hover:bg-primary/5 w-full"
                    onClick={() => {}}
                  >
                    查看全部消息
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          )}

          {/* User Menu */}
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-9 w-9">
                  <User className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5 text-sm">
                  <div className="font-medium">{profile?.username || '用户'}</div>
                  <div className="text-xs text-muted-foreground">{profile?.email || profile?.phone}</div>
                  {profile?.role === 'admin' && (
                    <div className="mt-1">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary/10 text-primary">
                        管理员
                      </span>
                    </div>
                  )}
                </div>
                <DropdownMenuSeparator />
                {config?.ai_provider && (
                  <div className="px-2 py-1 flex items-center gap-2">
                    <Zap className="h-3 w-3 text-amber-500" />
                    <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-tighter">
                      AI 引擎: {config.ai_provider}
                    </span>
                  </div>
                )}
                <DropdownMenuItem asChild>
                  <Link to="/profile" className="cursor-pointer">
                    <User className="mr-2 h-4 w-4" />
                    个人中心
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/membership" className="cursor-pointer">
                    <Crown className="mr-2 h-4 w-4 text-secondary" />
                    会员中心
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/tasks" className="cursor-pointer">
                    <Trophy className="mr-2 h-4 w-4 text-amber-500" />
                    每日任务
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/achievements" className="cursor-pointer">
                    <Award className="mr-2 h-4 w-4 text-purple-500" />
                    成就墙
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/template-market" className="cursor-pointer">
                    <Store className="mr-2 h-4 w-4 text-blue-500" />
                    模板市场
                  </Link>
                </DropdownMenuItem>
                {profile?.role === 'admin' && (
                  <DropdownMenuItem asChild>
                    <Link to="/admin" className="cursor-pointer">
                      <Settings className="mr-2 h-4 w-4" />
                      后台管理
                    </Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleSignOut} className="cursor-pointer text-red-600">
                  <LogOut className="mr-2 h-4 w-4" />
                  退出登录
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button variant="ghost" size="sm" asChild>
              <Link to="/login">
                <User className="h-4 w-4 mr-2" />
                登录
              </Link>
            </Button>
          )}

          {/* Mobile Menu */}
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="h-9 w-9 lg:hidden">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] sm:w-[400px]">
              <SheetHeader>
                <SheetTitle className="text-left gradient-text">所有分类</SheetTitle>
              </SheetHeader>
              <div className="grid gap-4 py-6">
                {INITIAL_CATEGORIES.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.id}`}
                    className={cn(
                      "flex items-center gap-3 px-4 py-3 text-lg font-medium rounded-lg transition-colors",
                      location.pathname === `/category/${cat.id}`
                        ? "bg-primary/10 text-primary"
                        : "hover:bg-muted"
                    )}
                  >
                    {cat.name}
                  </Link>
                ))}
                <Link
                  to="/ai-hub"
                  className={cn(
                    "flex items-center gap-3 px-4 py-3 text-lg font-medium rounded-lg transition-colors",
                    location.pathname === '/ai-hub'
                      ? "bg-primary/10 text-primary"
                      : "hover:bg-muted"
                  )}
                >
                  <Sparkles className="h-5 w-5" />
                  AI 助手
                </Link>
                <Link
                  to="/ai-creator"
                  className={cn(
                    "flex items-center gap-3 px-4 py-3 text-lg font-medium rounded-lg transition-colors",
                    location.pathname === '/ai-creator'
                      ? "bg-primary/10 text-primary"
                      : "hover:bg-muted"
                  )}
                >
                  <Zap className="h-5 w-5" />
                  AI 创作
                </Link>
                <div className="h-px bg-border my-2" />
                <Link
                  to="/profile"
                  className={cn(
                    "flex items-center gap-3 px-4 py-3 text-lg font-medium rounded-lg transition-colors",
                    location.pathname === '/profile'
                      ? "bg-primary/10 text-primary"
                      : "hover:bg-muted"
                  )}
                >
                  <User className="h-5 w-5" />
                  个人中心
                </Link>
                <Link
                  to="/membership"
                  className={cn(
                    "flex items-center gap-3 px-4 py-3 text-lg font-medium rounded-lg transition-colors",
                    location.pathname === '/membership'
                      ? "bg-primary/10 text-primary"
                      : "hover:bg-muted"
                  )}
                >
                  <Crown className="h-5 w-5 text-secondary" />
                  会员中心
                </Link>
                <Link
                  to="/resources"
                  className={cn(
                    "flex items-center gap-3 px-4 py-3 text-lg font-medium rounded-lg transition-colors",
                    location.pathname === '/resources'
                      ? "bg-primary/10 text-primary"
                      : "hover:bg-muted"
                  )}
                >
                  <Compass className="h-5 w-5" />
                  资源导航
                </Link>
                <Link
                  to="/browser"
                  className={cn(
                    "flex items-center gap-3 px-4 py-3 text-lg font-medium rounded-lg transition-colors",
                    location.pathname === '/browser'
                      ? "bg-primary/10 text-primary"
                      : "hover:bg-muted"
                  )}
                >
                  <Globe className="h-5 w-5" />
                  内置浏览器
                </Link>
                <Link
                  to="/search"
                  className={cn(
                    "flex items-center gap-3 px-4 py-3 text-lg font-medium rounded-lg transition-colors",
                    location.pathname === '/search'
                      ? "bg-primary/10 text-primary"
                      : "hover:bg-muted"
                  )}
                >
                  <Search className="h-5 w-5" />
                  全局搜索
                </Link>
                <Link
                  to="/unlock"
                  className={cn(
                    "flex items-center gap-3 px-4 py-3 text-lg font-medium rounded-lg transition-colors",
                    isUnlocked
                      ? "border-green-500/50 text-green-600 dark:text-green-400 bg-green-500/10"
                      : "border-amber-500/50 text-amber-600 dark:text-amber-400 hover:bg-amber-500/10",
                    location.pathname === '/unlock' && "ring-2 ring-offset-2"
                  )}
                >
                  {isUnlocked ? (
                    <>
                      <Check className="h-5 w-5" />
                      高级模式（已解锁）
                    </>
                  ) : (
                    <>
                      <Crown className="h-5 w-5" />
                      高级解锁模式
                    </>
                  )}
                </Link>
                <div className="h-px bg-border my-2" />
                <div className="flex items-center justify-between px-4">
                  <span className="text-sm font-medium text-muted-foreground">外观模式</span>
                  <ModeToggle />
                </div>
                <Link to="/admin" className="px-4 py-3 text-sm text-muted-foreground hover:text-primary transition-colors">
                  进入后台管理
                </Link>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
};
